// screens/QRPaymentReport.jsx

import React, { useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Button,
  Chip,
  Box,
  Stack,
  TextField,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import moment from "moment";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import CustomTabs from "components/Shared/CustomTabs";
import HighlightedText from "components/Shared/HighlightedText";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import TableFilter from "components/Shared/AppTable/TableFilter";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { addDays, differenceInDays } from "date-fns";
import BaseSelect from "components/Shared/Base/BaseSelect";

import { useAppMeta, useTablePagination } from "hooks";
import { useLazyListQRPaymentCollectionsQuery } from "store/services/reportApi";
import { localizeDateTimeShort } from "utils/dateFormats";
import { exportExcelFile } from "utils/exportExcelFile";

const getStatusStyle = (status) => {
  const styles = {
    PENDING: { backgroundColor: "#FFF3CD", textColor: "#856404" },
    APPROVED: { backgroundColor: "#DFF7DF", textColor: "green" },
    REJECTED: { backgroundColor: "#FDEDEC", textColor: "#E74C3C" },
  };

  return styles[status?.toUpperCase()] || { backgroundColor: "#F8F9F9", textColor: "#7F8C8D" };
};

const QRPaymentReport = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();

  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "All";
  const keyword = searchParams.get("search") || "";

  // Date range state (like VendorRMWiseReportDetail)
  const [timeRange, setTimeRange] = useState("today");
  const [customFromDate, setCustomFromDate] = useState(null);
  const [customToDate, setCustomToDate] = useState(null);
  // These are the dates actually used for fetching — only updated on "Apply" for custom range
  const [appliedFromDate, setAppliedFromDate] = useState(null);
  const [appliedToDate, setAppliedToDate] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  // Date range calculation
  const getDateRange = useCallback((range, from, to) => {
    const today = moment().startOf("day");
    switch (range) {
      case "today":
        return {
          fromDate: today.format("MM-DD-YYYY"),
          toDate: today.endOf("day").format("MM-DD-YYYY"),
        };
      case "last7days":
        return {
          fromDate: moment().subtract(6, "days").startOf("day").format("MM-DD-YYYY"),
          toDate: today.endOf("day").format("MM-DD-YYYY"),
        };
      case "lastMonth":
        return {
          fromDate: moment().subtract(1, "month").startOf("month").format("MM-DD-YYYY"),
          toDate: moment().subtract(1, "month").endOf("month").format("MM-DD-YYYY"),
        };
      case "custom":
        if (from && to && moment(from).isValid() && moment(to).isValid()) {
          if (differenceInDays(new Date(to), new Date(from)) > 90) {
            setErrorMessage("Date range cannot exceed 90 days.");
            return { fromDate: null, toDate: null };
          }
          return {
            fromDate: moment(from).format("MM-DD-YYYY"),
            toDate: moment(to).format("MM-DD-YYYY"),
          };
        }
        setErrorMessage("Invalid date range. Ensure 'To' date is after 'From' date.");
        return { fromDate: null, toDate: null };
      default:
        return { fromDate: null, toDate: null };
    }
  }, []);

  // Custom range is valid when both dates are set, ordered, and within 90 days
  const isCustomRangeValid =
    customFromDate &&
    customToDate &&
    moment(customFromDate).isValid() &&
    moment(customToDate).isValid() &&
    moment(customToDate).isSameOrAfter(moment(customFromDate), "day") &&
    differenceInDays(customToDate, customFromDate) <= 90;

  // Handle time range change
  const handleTimeRangeChange = (e) => {
    const value = e.target.value;
    setTimeRange(value);
    if (value !== "custom") {
      setCustomFromDate(null);
      setCustomToDate(null);
      setAppliedFromDate(null);
      setAppliedToDate(null);
    }
  };

  const breadcrumbs = [{
    title: "Reports",
    path: "/app/crm/reports/list",
  }, { title: "QR Payment Collections" }];

  const paymentCollectionTabs = [
    { label: "All", value: "All" },
    { label: "Counter Collection", value: "COC Collection" },
    { label: "Delivery Collection", value: "Delivery Collection" },
  ];

  const columns = [
    // ID Column - conditional based on tab
    ...(activeTab === "All"
      ? [
        {
          name: "ID",
          selector: (row) => row.collectionId,
          cell: (row) => {
            const linkPath = row.collectionType === "Counter Collection"
              ? `/app/crm/coc-collection/${row._id}/detail`
              : `/app/crm/delivery-collection/${row._id}/detail`;
            return (
              <Link
                to={linkPath}
                className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
                title={row.collectionId}
              >
                {row.collectionId || "N/A"}
              </Link>
            );
          },
          width: "150px",
        },
      ]
      : activeTab === "COC Collection"
        ? [
          {
            name: "COC ID",
            selector: (row) => row.cocCollectionId,
            cell: (row) => (
              <Link
                to={`/app/crm/coc-collection/${row._id}/detail`}
                className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
                title={row.cocCollectionId}
              >
                {row.cocCollectionId || "N/A"}
              </Link>
            ),
            width: "150px",
          },
        ]
        : [
          {
            name: "Delivery ID",
            selector: (row) => row.deliveryCollectionId,
            cell: (row) => (
              <Link
                to={`/app/crm/delivery-collection/${row._id}/detail`}
                className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
                title={row.deliveryCollectionId}
              >
                {row.deliveryCollectionId || "N/A"}
              </Link>
            ),
            width: "150px",
          },
        ]),
    {
      name: "Shipment ID",
      selector: (row) => row.merchantBillNo,
      cell: (row) =>
        row.merchantBillNo ? (
          <HighlightedText text={row.merchantBillNo} highlight={keyword} />
        ) : (
          "N/A"
        ),
      width: "150px",
    },
    {
      name: "Transaction ID",
      selector: (row) => row.transactionId,
      cell: (row) =>
        row?.transactionId ? (
          <HighlightedText text={row?.transactionId} highlight={keyword} />
        ) : (
          "N/A"
        ),
      sortable: true,
      width: "180px",
    },
    {
      name: "Amount",
      selector: (row) => row.amount,
      cell: (row) => (row.amount ? `Rs. ${row.amount.toLocaleString()}` : "N/A"),
      sortable: true,
      width: "120px",
    },
    {
      name: "Payer Name",
      selector: (row) => row.payerName,
      cell: (row) => row.payerName || "N/A",
      width: "150px",
    },
    {
      name: "Merchant Name",
      selector: (row) => row.merchantName,
      cell: (row) => row.merchantName || "N/A",
      width: "200px",
    },
    {
      name: "Payment Date",
      selector: (row) => row.paymentDate,
      cell: (row) => <AppTableDateTime date={row?.paymentDate} />,
      sortable: true,
      width: "160px",
    },
    {
      name: "Added By",
      selector: (row) => row.addedUser?.name,
      cell: (row) => row.addedUser?.name || "N/A",
      width: "150px",
    },
    {
      name: "Collection Status",
      selector: (row) => row.isCollected,
      cell: (row) => (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Chip
            label={row?.isCollected?.toUpperCase() || "N/A"}
            style={{
              backgroundColor: getStatusStyle(row?.isCollected).backgroundColor,
              color: getStatusStyle(row?.isCollected).textColor,
              fontWeight: "bold",
              fontSize: "0.7rem",
              height: "20px",
              padding: "0 6px",
              borderRadius: "12px",
              width: "auto",
            }}
          />
        </Box>
      ),
      width: "150px",
    },
    {
      name: "Collected Date",
      selector: (row) => row.collectedDate,
      cell: (row) => <AppTableDateTime date={row?.collectedDate} />,
      sortable: true,
      width: "160px",
    },
    ...(activeTab === "All"
      ? [
        {
          name: "Collection Type",
          selector: (row) => row.collectionType,
          cell: (row) => (
            <Box display="flex" justifyContent="center" alignItems="center">
              <Chip
                label={row?.collectionType || "N/A"}
                size="small"
                style={{
                  fontWeight: "bold",
                  fontSize: "0.7rem",
                }}
              />
            </Box>
          ),
          width: "180px",
        },
      ]
      : []),
  ];

  // RTK Query
  const [fetchPaymentCollections, { data: paymentRes, isLoading, isFetching, error }] =
    useLazyListQRPaymentCollectionsQuery();

  React.useEffect(() => {
    // For custom range, don't hit the API until both dates are applied via the Apply button
    if (timeRange === "custom" && (!appliedFromDate || !appliedToDate)) {
      return;
    }

    const { fromDate, toDate } = getDateRange(timeRange, appliedFromDate, appliedToDate);
    // Backend expects ISO (YYYY-MM-DD) dates for new Date() parsing
    const startDate = fromDate ? moment(fromDate, "MM-DD-YYYY").format("YYYY-MM-DD") : undefined;
    const endDate = toDate ? moment(toDate, "MM-DD-YYYY").format("YYYY-MM-DD") : undefined;

    fetchPaymentCollections({
      active: "true",
      tab: activeTab,
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
    });
  }, [
    activeTab,
    currentPage,
    fetchPaymentCollections,
    keyword,
    perPage,
    timeRange,
    appliedFromDate,
    appliedToDate,
    getDateRange,
  ]);

  // Excel Export Handler
  const handleExcelExport = async () => {
    try {
      // For custom range, use the applied dates so the export matches the visible list
      if (timeRange === "custom" && (!appliedFromDate || !appliedToDate)) {
        return;
      }

      const { fromDate, toDate } = getDateRange(timeRange, appliedFromDate, appliedToDate);
      const startDate = fromDate ? moment(fromDate, "MM-DD-YYYY").format("YYYY-MM-DD") : undefined;
      const endDate = toDate ? moment(toDate, "MM-DD-YYYY").format("YYYY-MM-DD") : undefined;

      // Fetch all records for the current tab and date range
      const response = await fetchPaymentCollections({
        active: "true",
        tab: activeTab,
        exportAll: "true", // This tells backend to return all records
        ...(keyword && { keyword }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
      }).unwrap();

      const exportData = response?.data?.map((row) => {
        const baseData = {
          "Transaction ID": row.transactionId || "N/A",
          Amount: row.amount || 0,
          "Payer Name": row.payerName || "N/A",
          "Payer Phone": row.payerPhone || "N/A",
          "Merchant Name": row.merchantName || "N/A",
          "Merchant Bill No": row.merchantBillNo || "N/A",
          "Payment Date": row.paymentDate ? localizeDateTimeShort(row.paymentDate) : "N/A",
          "Added By": row.addedUser?.name || "N/A",
          "Collection Status": row.isCollected || "N/A",
          "Collected Date": row.collectedDate ? localizeDateTimeShort(row.collectedDate) : "N/A",
        };

        // Add ID column based on tab
        if (activeTab === "All") {
          return {
            ID: row.collectionId || "N/A",
            ...baseData,
            "Collection Type": row.collectionType || "N/A",
          };
        } else if (activeTab === "COC Collection") {
          return {
            "COC ID": row.cocCollectionId || "N/A",
            ...baseData,
          };
        } else {
          return {
            "Delivery ID": row.deliveryCollectionId || "N/A",
            ...baseData,
          };
        }
      });

      const dateSuffix =
        fromDate && fromDate !== "null" && toDate && toDate !== "null"
          ? `_From_${fromDate}_to_${toDate}`
          : "";
      const fileName = `QR_Payment_Report_${activeTab}${dateSuffix}`;

      exportExcelFile({
        sheetData: exportData,
        fileName,
      });
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  useAppMeta(`${activeTab} Payment Collections`);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <BreadcrumbLayout breadcrumbs={breadcrumbs}>
        <BreadcrumbLayout.Error error={error} />
        <BreadcrumbLayout.Paper>
          <CustomTabs tabs={paymentCollectionTabs} activeTab={activeTab} />

          {/* Date Range Filter */}
          <Box sx={{ p: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            {/* Range */}
            <BaseSelect
              label="Range"
              value={timeRange}
              onChange={handleTimeRangeChange}
              options={[
                { label: "Today", value: "today" },
                { label: "Last 7 Days", value: "last7days" },
                { label: "Last Month", value: "lastMonth" },
                { label: "Custom", value: "custom" },
              ]}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.value}
              size="small"
              sx={{ minWidth: 140 }}
              disabled={isLoading || isFetching}
            />

            {/* Custom Date Pickers */}
            {timeRange === "custom" && (
              <Stack direction="row" spacing={1} alignItems="center">
                <DatePicker
                  label="From"
                  value={customFromDate}
                  onChange={(newValue) => {
                    setCustomFromDate(newValue);
                    if (customToDate && differenceInDays(customToDate, newValue) > 90) {
                      setCustomToDate(addDays(newValue, 90));
                      setErrorMessage("Date range adjusted to maximum 90 days.");
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} size="small" sx={{ width: 140 }} />
                  )}
                  maxDate={customToDate || new Date()}
                  disableFuture
                  disabled={isLoading || isFetching}
                />
                <DatePicker
                  label="To"
                  value={customToDate}
                  onChange={(newValue) => {
                    setCustomToDate(newValue);
                    if (customFromDate && differenceInDays(newValue, customFromDate) > 90) {
                      setCustomFromDate(addDays(newValue, -90));
                      setErrorMessage("Date range adjusted to maximum 90 days.");
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} size="small" sx={{ width: 140 }} />
                  )}
                  minDate={customFromDate}
                  maxDate={new Date()}
                  disableFuture
                  disabled={isLoading || isFetching}
                />
                <Tooltip
                  title={
                    isCustomRangeValid
                      ? "Apply date range"
                      : "Select a valid range (max 90 days)"
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={!isCustomRangeValid}
                      onClick={() => {
                        setAppliedFromDate(customFromDate);
                        setAppliedToDate(customToDate);
                      }}
                    >
                      Apply
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={handleExcelExport}
            >
              Export to Excel
            </Button>
          </Box>

          <TableFilter
            searchLabel="Search by Shipment ID, Payer, Merchant"
            disabled={isLoading || isFetching}
          />
          <AppTable
            columns={columns}
            data={paymentRes?.data ?? []}
            progressPending={isLoading || isFetching}
            paginationServer
            paginationPerPage={perPage}
            paginationTotalRows={paymentRes?.totalItems ?? 0}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
          />
        </BreadcrumbLayout.Paper>

        <Snackbar
          open={!!errorMessage}
          autoHideDuration={6000}
          onClose={() => setErrorMessage("")}
        >
          <Alert severity="error" onClose={() => setErrorMessage("")}>
            {errorMessage}
          </Alert>
        </Snackbar>
      </BreadcrumbLayout>
    </LocalizationProvider>
  );
};

export default QRPaymentReport;